import { StringUtils } from '@manifoldxyz/js-ts-utils';
import { MediaDisplay } from '@manifoldxyz/react-component-library';

interface MediaLightboxProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl?: string;
  animationUrl?: string;
}

export default function MediaLightbox({
  isOpen,
  onClose,
  imageUrl,
  animationUrl,
}: MediaLightboxProps) {
  if (!isOpen) return null;

  // Handle keyboard events for accessibility
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <>
      {/* Backdrop - full screen with blur effect */}
      <div
        className={StringUtils.classnames(
          'm-claim--fixed',
          'm-claim--inset-0',
          'm-claim--z-[1000000]',
        )}
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(4px)',
        }}
        onClick={onClose}
        role="presentation"
      />

      {/* Lightbox container */}
      <div
        className={StringUtils.classnames(
          'm-claim--fixed',
          'm-claim--inset-0',
          'm-claim--z-[1000000]',
          'm-claim--flex',
          'm-claim--items-center', // Center vertically
          'm-claim--justify-center', // Center horizontally
          'm-claim--p-4', // Add padding
        )}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        onClick={onClose}
      >
        {/* Media container */}
        <div
          className={StringUtils.classnames(
            'm-claim--relative',
            'm-claim--flex',
            'm-claim--flex-col',
            'm-claim--items-center',
            'm-claim--justify-center',
            'm-claim--w-full',
          )}
        >
          {/* Close button - Enhanced for visibility */}
          <button
            onClick={onClose}
            className={StringUtils.classnames(
              'm-claim--bg-surface-contrast',
              'm-claim--fixed', // Changed from absolute to fixed
              'm-claim--right-8',
              'm-claim--top-8',
              'm-claim--p-2',
              'm-claim--text-contrast',
              'm-claim--z-[1000001]', // Ensure it's above all content
              'm-claim--rounded-full',
              'm-claim--border-default', // Add border
              'm-claim--shadow-lg', // Add shadow
              'm-claim--shadow-black/50', // Shadow color
              'focus:m-claim--outline-none',
              'focus:m-claim--ring-2',
              'focus:m-claim--ring-white/50', // Focus ring for accessibility
              'm-claim--transition-transform',
              'hover:m-claim--scale-110',
            )}
            aria-label="Close fullscreen view"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="m-claim--size-6 m-claim--text-contrast"
            >
              <path
                d="M6 18L18 6M6 6L18 18"
                stroke="currentColor"
                strokeWidth="2.5" // Increased stroke width for better visibility
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {/* Media content */}
          <div
            className="m-claim--size-full"
            onClick={
              // @important: enforce that clicking on the media does not close the lightbox
              // via bubbling up the event to the backdrop
              (event) => event.stopPropagation()
            }
          >
            <MediaDisplay
              imageUrl={imageUrl || ''}
              animationUrl={animationUrl}
              mediaStyle={{
                maxWidth: '100dvw',
                maxHeight: '100dvh', // Ensure there's room for controls
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
}
